import Styles from './CustomizeSchedule.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { useEffect, useState } from 'react'
import { WeekDaysInput } from './WeekDaysInput'
import { DailyFrequencyInput } from './DailyFrequencyInput'
import { DaysBeforeReviewInput } from './DaysBeforeReviewInput'
import { StartDateInput } from './StartDateInput'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import dayjs from 'dayjs'
import { PlanLengthInput } from './PlanLengthInput'
import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import { useQueryClient } from 'react-query'
import { DailyFrequency, WeekDays } from 'types'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { Explanation } from 'flows/common/components/Explanation/Explanation'
import { useWeekDaysLabel } from 'screens/BrandKits/components/BrandPlanTimeline/BrandPlanScheduleEditModal/useWeekDaysLabel'
import { Callout } from 'components/FirstTimeExperienceModal/components/Callout/Callout'
import { StrategyBatchSchedule } from 'screens/VirtualMarketerOnboarding/CustomizeSchedule/StrategyBatchSchedule'

interface Props {
  onNext: () => void
  onBack: () => void
  brandPlanId: number
}

export const WEEK_DAYS: WeekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export const ONLY_WEEK_DAYS: WeekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
export const ONLY_WEEKENDS: WeekDays = ['Saturday', 'Sunday']

export const CustomizeSchedule = ({ onNext, onBack, brandPlanId }: Props) => {
  const queryClient = useQueryClient()
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlanId })
  const { data: brandPlan } = useBrandPlan(brandPlanId, { cacheTime: 60 * 1000 * 5 })

  // Determine which pill to show as selected based on the days of the week
  const initialWeekDaysPill = useWeekDaysLabel({
    weekDays: brandPlan?.week_days || ONLY_WEEK_DAYS,
  })

  const defaultStartDate = dayjs().add(1, 'day').format('YYYY-MM-DD')

  const [startDate, setStartDate] = useState<string>(brandPlan?.start_date || defaultStartDate)
  const [weekDaysPill, setWeekDaysPill] = useState<string>(initialWeekDaysPill)
  const [weekDays, setWeekDays] = useState<WeekDays>(brandPlan?.week_days || ONLY_WEEK_DAYS)
  const [daysBeforeReview, setDaysBeforeReview] = useState<number>(brandPlan?.days_before_review || 2)
  const [dailyFrequency, setDailyFrequency] = useState<DailyFrequency>(brandPlan?.daily_frequency || 'once_a_day')
  const [planLength, setPlanLength] = useState<number>(brandPlan?.length || 6)
  const [scheduleLength, setScheduleLength] = useState<number>(brandPlan?.schedule_length || 1)

  // Set initial values when brand plan is loaded
  useEffect(() => {
    setWeekDaysPill(initialWeekDaysPill)
    setWeekDays(brandPlan?.week_days?.length ? brandPlan.week_days : ONLY_WEEK_DAYS)
    setStartDate(brandPlan?.start_date || defaultStartDate)
    setDaysBeforeReview(brandPlan?.days_before_review || 2)
    setDailyFrequency(brandPlan?.daily_frequency || 'high')
    setPlanLength(brandPlan?.length || 6)
    setScheduleLength(brandPlan?.schedule_length || 1)
  }, [brandPlan?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!brandPlan) return null

  const handleNext = async () => {
    await updateBrandPlan({
      start_date: startDate,
      week_days: weekDays,
      days_before_review: daysBeforeReview,
      daily_frequency: dailyFrequency,
      length: planLength,
      schedule_length: scheduleLength,
    })
    queryClient.setQueryData(['brandPlan', brandPlanId], {
      ...brandPlan,
      start_date: startDate,
      week_days: weekDays,
      days_before_review: daysBeforeReview,
      daily_frequency: dailyFrequency,
      length: planLength,
      schedule_length: scheduleLength,
    })
    onNext()
  }

  return (
    <MultiStepStepLayout
      title="Set your schedule"
      scrollContainerClassName={Styles.scrollContainer}
      contentClassName={VirtualMarketerOnboardingStyles.content}
      description={
        <Callout>
          <Explanation icon="🗓️">
            You’ll be able to update these preferences anytime in your Brand Kit after onboarding.
          </Explanation>
        </Callout>
      }
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <Button variant="tertiary" size="xl" onPress={onBack} frontIcon={ArrowLeft} aria-label="Back">
                Back
              </Button>
              <Button size="xl" onPress={handleNext} endIcon={ArrowRight} aria-label="Continue">
                Continue
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.wrapper}>
          <StrategyBatchSchedule
            strategy={brandPlan.strategy}
            length={scheduleLength}
            onLengthChange={setScheduleLength}
          />
          <div className={Styles.container}>
            <div className={Styles.timelineContainer}>
              <div className={Styles.circle} />
              <div className={cx(Styles.line, Styles.line1)} />
              <div className={Styles.circle} />
              <div
                className={cx(Styles.line, Styles.line2, {
                  [Styles.long]: weekDaysPill === 'custom' && weekDays.length > 5,
                })}
              />
              <div className={Styles.circle} />
              <div className={cx(Styles.line, Styles.line3)} />
              <div className={Styles.triangle} />
            </div>
            <div className={Styles.root}>
              <Text variant="label" color="var(--dark-40)">
                Today
              </Text>
              <StartDateInput startDate={startDate} onStartDateChange={setStartDate} />
              <DaysBeforeReviewInput
                daysBeforeReview={daysBeforeReview}
                onDaysBeforeReviewChange={setDaysBeforeReview}
                startDate={startDate}
              />
              <Text variant="label" color="var(--dark-40)">
                Week 1
              </Text>
              <WeekDaysInput
                weekDays={weekDays}
                onWeekDaysChange={(weekDays) => {
                  setWeekDays(weekDays.sort((a, b) => WEEK_DAYS.indexOf(a) - WEEK_DAYS.indexOf(b)))
                }}
                weekDaysPill={weekDaysPill}
                onWeekDaysPillChange={setWeekDaysPill}
              />
              <DailyFrequencyInput dailyFrequency={dailyFrequency} onDailyFrequencyChange={setDailyFrequency} />
              <Text variant="label" color="var(--dark-40)">
                Week 2
              </Text>
              <PlanLengthInput planLength={planLength} onPlanLengthChange={setPlanLength} startDate={startDate} />
            </div>
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
